import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import TaskMap from "@/components/TaskMap";
import MultiSelect from "@/components/MultiSelect";
import { ChevronDown, ChevronRight, Trash2, Car, Plane, PackageCheck, ClipboardList, Save, Loader2, Download, Copy, GripVertical, ArrowLeftRight } from "lucide-react";

export const TYPES = [
  { key: "driving", label: "Driving", icon: Car, cls: "bg-blue-100 text-blue-700" },
  { key: "flight", label: "Flight", icon: Plane, cls: "bg-lime-100 text-lime-700" },
  { key: "pickup", label: "Pickup", icon: PackageCheck, cls: "bg-violet-100 text-violet-700" },
  { key: "general", label: "General", icon: ClipboardList, cls: "bg-slate-100 text-slate-600" },
];
export const byType = Object.fromEntries(TYPES.map((t) => [t.key, t]));

export default function TaskItem({
  t, idx, isOpen, savingId, overIdx, employees, center,
  onToggleOpen, onToggleDone, onCopy, onRemove, onPatch, onSave, onExport,
  onDragStart, onDragOver, onDrop, onDragEnd,
}) {
  const T = byType[t.type] || byType.general;
  const crewIds = (t.assignee_ids && t.assignee_ids.length) ? t.assignee_ids : (t.assignee_id ? [t.assignee_id] : []);
  const crewNames = crewIds.map((id) => employees.find((e) => e.id === id)?.name).filter(Boolean);

  return (
    <div className={`border rounded-lg overflow-hidden transition-colors ${overIdx === idx ? "border-lime-400" : "border-slate-200"}`} data-testid={`task-${t.id}`}>
      <div
        className="flex items-center gap-2 px-3 py-2.5 bg-slate-50"
        draggable
        onDragStart={() => onDragStart(idx)}
        onDragOver={(e) => onDragOver(e, idx)}
        onDrop={() => onDrop(idx)}
        onDragEnd={onDragEnd}
      >
        <GripVertical className="h-4 w-4 text-slate-300 cursor-grab shrink-0" data-testid={`task-drag-${t.id}`} />
        <Checkbox checked={!!t.done} onCheckedChange={() => onToggleDone(t)} data-testid={`task-done-${t.id}`} className="data-[state=checked]:bg-lime-500 data-[state=checked]:border-lime-500" />
        <button onClick={() => onToggleOpen(t.id)} data-testid={`task-toggle-${t.id}`} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${T.cls}`}><T.icon className="h-3 w-3" /> {T.label}</span>
          <span className={`text-sm font-medium truncate ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.name}</span>
          {crewNames.length ? <span className="text-xs text-slate-400 truncate">· {crewNames.join(", ")}</span> : null}
        </button>
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid={`task-copy-${t.id}`} title="Copy task" className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-200 hover:text-slate-700"><Copy className="h-4 w-4" /></button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-48 p-1">
            <button onClick={() => onCopy(t, false)} data-testid={`task-copy-plain-${t.id}`} className="w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm text-slate-700 hover:bg-slate-50"><Copy className="h-4 w-4" /> Copy task</button>
            <button onClick={() => onCopy(t, true)} data-testid={`task-copy-reverse-${t.id}`} className="w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm text-slate-700 hover:bg-slate-50"><ArrowLeftRight className="h-4 w-4" /> Copy in reverse (return leg)</button>
          </PopoverContent>
        </Popover>
        <button onClick={() => onRemove(t.id)} data-testid={`task-delete-${t.id}`} className="h-7 w-7 rounded-md flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>

      {isOpen && (
        <div className="p-3 space-y-3 border-t border-slate-100">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Task name</Label>
            <Input value={t.name || ""} onChange={(e) => onPatch(t.id, { name: e.target.value })} className="mt-1.5" data-testid={`task-edit-name-${t.id}`} placeholder="Task name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
              <Select value={t.type} onValueChange={(v) => onPatch(t.id, { type: v })}>
                <SelectTrigger data-testid={`task-edit-type-${t.id}`} className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Assigned crew</Label>
              <MultiSelect
                testid={`task-assignees-${t.id}`}
                placeholder="Assign people"
                value={crewIds}
                onChange={(v) => onPatch(t.id, { assignee_ids: v, assignee_id: v[0] || null })}
                options={employees.map((e) => ({ value: e.id, label: e.name }))}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
            <Textarea value={t.notes || ""} onChange={(e) => onPatch(t.id, { notes: e.target.value })} rows={2} className="mt-1.5" data-testid={`task-notes-${t.id}`} />
          </div>

          <TaskMap
            type={t.type}
            center={center}
            waypoints={t.waypoints || []}
            zone={t.zone || []}
            dangers={t.dangers || []}
            onChange={(geo) => onPatch(t.id, geo)}
          />

          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => onSave(t)} disabled={savingId === t.id} data-testid={`task-save-${t.id}`} className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold">
              {savingId === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save Task</>}
            </Button>
            {t.type === "flight" && (
              <>
                <button onClick={() => onExport(t, "gpx")} data-testid={`export-task-gpx-${t.id}`} className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"><Download className="h-3.5 w-3.5" /> OzRunways GPX</button>
                <button onClick={() => onExport(t, "kml")} data-testid={`export-task-kml-${t.id}`} className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200"><Download className="h-3.5 w-3.5" /> OzRunways KML</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
